import React, { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatChartDate } from '../utils/dateUtils';
import BloodTrendChart from './shared/BloodTrendChart';
import { filterByTimeWindow } from './TimeWindowSelector';

/**
 * Compute dashboard domain for nutrition charts
 */
const computeDashboardDomain = (records, country = 'AU') => {
  if (!records || records.length === 0) return null;
  
  const recordDates = records
    .map(r => r.date)
    .filter(Boolean)
    .map(date => ({
      date,
      timestamp: new Date(date).getTime()
    }))
    .filter(d => !isNaN(d.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp);
  
  if (recordDates.length === 0) return null;
  
  const domainStartTs = recordDates[0].timestamp;
  const domainEndTs = recordDates[recordDates.length - 1].timestamp;
  
  const dataPoints = recordDates.map(d => ({
    date: d.date,
    timestamp: d.timestamp,
    label: formatChartDate(d.date, country)
  }));
  
  return {
    domainStartTs,
    domainEndTs,
    dataPoints,
    margin: { left: 32, right: 0 }
  };
};

const DashboardNutritionGraphs = ({ records, timeWindow = 'all' }) => {
  const { user } = useAuth();
  const country = user?.country || 'AU';

  // Filter records by time window (presentation-layer only)
  const filteredRecords = useMemo(() => {
    return filterByTimeWindow(records, timeWindow);
  }, [records, timeWindow]);

  // Compute domain for consistent date formatting
  const dashboardDomain = useMemo(() => {
    return computeDashboardDomain(filteredRecords, country);
  }, [filteredRecords, country]);

  const getMarkerConfig = (markerId) => {
    const isUS = user?.country === 'US';
    const configs = {
      vitamin_d: { 
        label: 'Vitamin D', 
        color: '#F59E0B',
        unit: isUS ? 'ng/mL' : 'nmol/L',
        range: isUS ? { min: 20, max: 50 } : { min: 50, max: 125 }
      },
      b12: { 
        label: 'B12', 
        color: '#10B981',
        unit: isUS ? 'pg/mL' : 'pmol/L',
        range: isUS ? { min: 200, max: 900 } : { min: 150, max: 700 }
      },
      folate: { 
        label: 'Folate', 
        color: '#3B82F6',
        unit: isUS ? 'ng/mL' : 'nmol/L',
        range: isUS ? { min: 3, max: 20 } : { min: 7, max: 45 }
      },
      ferritin: { 
        label: 'Ferritin', 
        color: '#EF4444',
        unit: 'ng/mL',
        range: { min: 30, max: 300 }
      }
    };
    return configs[markerId];
  };

  const markers = ['vitamin_d', 'b12', 'folate', 'ferritin'];

  // Helper to check if a marker has data (in filtered records)
  const hasDataForMarker = (markerKey) => {
    return filteredRecords.some(r => r[markerKey] != null);
  };

  // Sort markers: ones with data first, then empty ones (maintaining relative order within each group)
  const sortedMarkers = [...markers].sort((a, b) => {
    const aHasData = hasDataForMarker(a);
    const bHasData = hasDataForMarker(b);
    if (aHasData && !bHasData) return -1;
    if (!aHasData && bHasData) return 1;
    return 0; // Keep original order within same group
  });

  const renderGraph = (markerKey) => {
    const marker = getMarkerConfig(markerKey);
    
    // Plot each value at its EXACT record date
    const chartData = filteredRecords
      .filter(r => r[markerKey] != null)
      .map(r => ({
        date: formatChartDate(r.date, country),
        fullDate: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[markerKey]
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    const hasData = chartData.length > 0;

    return (
      <div key={markerKey} className="bg-white rounded-xl px-0 py-4 md:px-3 md:py-6 border border-slate-200">
        <div className="mb-3 md:mb-4 px-3 md:px-3">
          <h3 className="text-base md:text-lg font-semibold text-slate-900">{marker.label} Trend ({marker.unit})</h3>
          <p className="text-xs md:text-sm text-slate-500">
            Reference range: {marker.range.min}-{marker.range.max}
          </p>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 px-3 md:px-3 flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: marker.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: marker.color }}
            ></div>
            <span>Your results</span>
          </div>
        </div>

        {/* Chart - pass dashboardDomain for consistent date formatting */}
        <BloodTrendChart
          data={chartData}
          markerConfig={marker}
          showLegend={false}
          pageDomain={dashboardDomain}
        />

        {!hasData && (
          <div className="mt-3 md:mt-4 text-center px-3 md:px-3">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Charts */}
      {sortedMarkers.map(marker => renderGraph(marker))}
    </div>
  );
};

export default DashboardNutritionGraphs;
